import { useState } from "react"

import Modal from "shared/components/modal"
import SuccessModal from "app/components/billing/success_modal"
import { changeBillingInterval } from "app/components/upgrade_plan_modal/pro_upgrade/api"

import iftttLogo from "images/logo-ifttt.svg?raw"
import { planNameBadge } from "shared/utils/tiers"

import styles from "app/components/upgrade_plan_modal/shared_modal.module.scss"

interface Props {
  monthlyPrice: string
  monthlyPriceId: string
  planName: string
}

const BillingSwitchToMonthlyModal = ({ monthlyPrice, monthlyPriceId, planName }: Props) => {
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  const handleLinkClick = (evt: MouseEvent) => {
    evt.preventDefault()
    setShowModal(true)
  }

  const handleClose = () => {
    setShowModal(false)
  }

  const handleSwitch = async () => {
    setLoading(true)

    try {
      const { errors } = await changeBillingInterval({
        targetInterval: "month",
        priceId: monthlyPriceId,
      })

      if (!errors?.length) {
        setShowModal(false)
        setShowSuccess(true)
      } else {
        const errorMessage = errors[0]?.message || "Something went wrong. Please try again."
        window.appendFlash(errorMessage, "danger")
        setShowModal(false)
      }
    } catch {
      window.appendFlash("Something went wrong. Please try again.", "danger")
      setShowModal(false)
    } finally {
      setLoading(false)
    }
  }

  if (showSuccess) {
    return (
      <SuccessModal
        title="Successfully updated to monthly"
        badge={iftttLogo + planNameBadge(planName)}
        successContent={
          <p>
            You&apos;ll receive a refund for the unused portion of your annual plan, and monthly billing will begin
            immediately.
          </p>
        }
        visible={true}
        onClose={() => window.location.reload()}
        buttons={[
          {
            label: "Okay",
            onClick: () => window.location.reload(),
            variant: "secondary",
          },
        ]}
      />
    )
  }

  return (
    <>
      <a onClick={handleLinkClick} className="link-standalone">
        Switch to monthly billing
      </a>
      {showModal && (
        <div className={styles["modal-container"]} data-fullscreen-modal>
          <Modal show={showModal} closeListener={handleClose} includeCloseX={true}>
            <h2 className="mt0 mb0">Switch to monthly billing</h2>
            <p>
              You will be billed {monthlyPrice} monthly to the payment method you have on file starting today. A refund
              for the unused portion of your annual plan will be applied to your payments.
            </p>
            <div className="action-buttons-row">
              <button className="button-tertiary button-outlined" onClick={handleClose} disabled={loading}>
                Nevermind
              </button>
              <button className="button-tertiary" disabled={loading} onClick={handleSwitch}>
                {loading ? "Switching..." : "Switch to monthly"}
              </button>
            </div>
          </Modal>
        </div>
      )}
    </>
  )
}

export default BillingSwitchToMonthlyModal
